import { useMutation, useQuery } from "@tanstack/react-query";
import useAxiosInstance from "./axiosInstance.utils";
import { errorFormat } from "./handleError.utils";
import { onSuccessMessage, queryClient } from "./reactQuery.utils";

/**
 * :::::::MUTATIONS:::::::::::
 */

export function useCreateRoleMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function createRoleApi(data) {
    try {
      const api = `/role`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["createRoleApi", ...keyData],
    mutationFn: (data) => createRoleApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("roles");
      onSuccessMessage(data, 201);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createRole: mutateAsync };
}

export function useUpdateRoleMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function updateRoleApi({ id, ...data }) {
    try {
      const api = `/role/${id}`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["updateRoleApi", ...keyData],
    mutationFn: (data) => updateRoleApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("roles");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, updateRole: mutateAsync };
}

export function useDeleteRoleMutation(keyData) {
  const { deleteApi } = useAxiosInstance();
  async function deleteRoleApi(id) {
    try {
      const api = `/role/${id}`;
      const apiResponse = await deleteApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["deleteRoleApi", ...keyData],
    mutationFn: (data) => deleteRoleApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("roles");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, deleteRole: mutateAsync };
}

export function useAssignRolePermissionMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function assignRolePermissionApi({ id, ...data }) {
    try {
      const api = `/role/assign/permission`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["assignRolePermissionApi", ...keyData],
    mutationFn: (data) => assignRolePermissionApi(data),
    onSuccess: (data) => {
      if (data?.status === 200) {
        queryClient.invalidateQueries("getAllRolePermissions");
      }
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, assignRolePermission: mutateAsync };
}

/**
 * :::::::QUERIES:::::::::::
 */
export function useGetAllRoleQuery({ query, options = {} }) {
  const { getApi } = useAxiosInstance();
  async function getAllRoleApi(query) {
    try {
      const api = `/roles${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { data, isPending: loading } = useQuery({
    queryKey: [`getAllRole-${query ?? ""}`],
    queryFn: () => getAllRoleApi(query),
    ...options
  });
  const newData = data?.data?.roles ?? {};
  return { data: newData, loading };
}

export function useGetAllRolePermissionsQuery({ slug, query, options = {} }) {
  const { getApi } = useAxiosInstance();
  async function getAllRolePermissionsApi(slug, query) {
    try {
      const api = `/role/permissions/${slug ?? ""}${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { data, isPending: loading } = useQuery({
    queryKey: [`getAllRolePermissions${slug}-${query ?? ""}`],
    queryFn: () => getAllRolePermissionsApi(slug, query),
    ...options
  });
  const newData = data?.data?.modules ?? {};
  return { data: newData, loading };
}
